import React, { useEffect, useState, useCallback } from "react";
import { Alert, Button, Card, Input, Space, Spin, Tag, Typography, Divider, message } from "antd";
import {
  KeyOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  DeleteOutlined,
  GithubOutlined,
} from "@ant-design/icons";
import {
  useLazyGetGitTokenQuery,
  usePutGitTokenMutation,
  useDeleteGitTokenMutation,
} from "../../../redux/gitToken/gitTokenAPI";
import GETJwtToken from "../../../redux/authentication/authentication";

const { Text, Title, Paragraph, Link } = Typography;

const GITHUB_PAT_URL = "https://github.com/settings/tokens";
const GITHUB_PAT_NEW_URL =
  "https://github.com/settings/tokens/new?scopes=repo&description=LiftNet+Schema+Editor";
const GITHUB_DOCS_URL =
  "https://docs.github.com/en/authentication/keeping-your-account-and-data-secure/managing-your-personal-access-tokens";

interface GitHubPatSetupProps {
  /**
   * Called with the plaintext PAT only when the user actively saves a new
   * token (the value is still in-memory from the input field). It is NOT
   * called on mount from a GET response because the backend no longer returns
   * the plaintext token after initial storage.
   *
   * TODO: Any feature that requires the PAT after initial save (e.g. direct
   * GitHub API calls from the client) must be proxied through the backend
   * instead of relying on this callback to supply the token later.
   */
  onTokenReady: (token: string) => void;
}

function GitHubPatSetup({ onTokenReady }: GitHubPatSetupProps) {
  const [patInput, setPatInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const [triggerGetGitToken, { data: tokenData, isError: fetchError }] = useLazyGetGitTokenQuery();
  const [triggerPutGitToken] = usePutGitTokenMutation();
  const [triggerDeleteGitToken] = useDeleteGitTokenMutation();

  // Fetch stored token status on mount.
  // The backend no longer returns the plaintext token in GET responses — only
  // has_token, token_preview, and expires_at are available. onTokenReady is
  // therefore NOT called here; it is only called when the user actively saves
  // a new token (see handleSave below).
  useEffect(() => {
    let cancelled = false;
    const fetchStatus = async () => {
      const jwt = await GETJwtToken();
      await triggerGetGitToken({ token: jwt });
      if (!cancelled) {
        setInitialLoading(false);
      }
    };
    fetchStatus();
    return () => {
      cancelled = true;
    };
  }, []);

  const isExpired =
    tokenData &&
    tokenData.has_token &&
    tokenData.expires_at &&
    new Date(tokenData.expires_at) < new Date();

  const hasValidToken = tokenData && tokenData.has_token && !isExpired;

  const handleSave = useCallback(async () => {
    const trimmed = patInput.trim();
    if (!trimmed) {
      message.warning("Please paste a GitHub Personal Access Token.");
      return;
    }

    if (!trimmed.startsWith("ghp_") && !trimmed.startsWith("github_pat_")) {
      message.warning(
        "This doesn't look like a GitHub PAT. Tokens typically start with ghp_ or github_pat_.",
      );
      return;
    }

    setSaving(true);
    try {
      const jwt = await GETJwtToken();
      const result: any = await triggerPutGitToken({
        token: jwt,
        body: { github_token: trimmed },
      });

      if (result.error) {
        const errorMsg =
          result.error.data?.message || result.error.data?.error || "Failed to save token.";
        message.error(errorMsg);
      } else {
        message.success("GitHub token saved successfully!");
        setPatInput("");
        onTokenReady(trimmed);
      }
    } catch (err: any) {
      message.error(err.message || "Failed to save token.");
    } finally {
      setSaving(false);
    }
  }, [patInput, triggerPutGitToken, onTokenReady]);

  const handleDelete = useCallback(async () => {
    try {
      const jwt = await GETJwtToken();
      await triggerDeleteGitToken({ token: jwt });
      message.success("GitHub token removed.");
    } catch (err: any) {
      message.error("Failed to remove token.");
    }
  }, [triggerDeleteGitToken]);

  if (initialLoading) {
    return (
      <div style={{ textAlign: "center", padding: 48 }}>
        <Spin tip="Checking GitHub token..." />
      </div>
    );
  }

  // If the token is valid and we already called onTokenReady, don't render the setup form
  if (hasValidToken) {
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <CheckCircleOutlined style={{ color: "#52c41a", fontSize: 18 }} />
          <Text>
            GitHub token stored
            {tokenData.token_preview && (
              <Text code style={{ marginLeft: 8 }}>
                {tokenData.token_preview}
              </Text>
            )}
          </Text>
          {tokenData.expires_at && (
            <Tag color="blue">Expires {new Date(tokenData.expires_at).toLocaleDateString()}</Tag>
          )}
          <Button size="small" danger icon={<DeleteOutlined />} onClick={handleDelete}>
            Remove
          </Button>
        </Space>
      </Card>
    );
  }

  // Show setup form when no token or expired
  return (
    <Card>
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <div>
          <Title level={4}>
            <GithubOutlined style={{ marginRight: 8 }} />
            GitHub Personal Access Token Required
          </Title>
          <Paragraph>
            The schema editor needs a GitHub Personal Access Token (PAT) to create pull requests and
            merge changes to the <Text strong>flatbuffer_schemas</Text> repository. Your token will
            be stored securely and reused for future edits.
          </Paragraph>
        </div>

        {isExpired && (
          <Alert
            type="warning"
            showIcon
            icon={<WarningOutlined />}
            message="Your stored GitHub token has expired"
            description="Please generate a new token and paste it below."
          />
        )}

        {fetchError && (
          <Alert
            type="error"
            showIcon
            message="Could not check token status"
            description="There was an error reaching the server. You can still enter a token below."
          />
        )}

        <Card type="inner" title="How to generate a Personal Access Token" size="small">
          <ol style={{ paddingLeft: 20, margin: 0 }}>
            <li style={{ marginBottom: 8 }}>
              Go to{" "}
              <Link href={GITHUB_PAT_URL} target="_blank" rel="noopener noreferrer">
                GitHub Settings &rarr; Developer settings &rarr; Personal access tokens
              </Link>
            </li>
            <li style={{ marginBottom: 8 }}>
              Click{" "}
              <Link href={GITHUB_PAT_NEW_URL} target="_blank" rel="noopener noreferrer">
                Generate new token (classic)
              </Link>
            </li>
            <li style={{ marginBottom: 8 }}>
              Give it a descriptive name (e.g. <Text code>LiftNet Schema Editor</Text>)
            </li>
            <li style={{ marginBottom: 8 }}>Set an expiration (90 days recommended)</li>
            <li style={{ marginBottom: 8 }}>
              Under <Text strong>Select scopes</Text>, check <Text code>repo</Text> (Full control of
              private repositories)
            </li>
            <li style={{ marginBottom: 8 }}>
              Click <Text strong>Generate token</Text> and copy the token immediately &mdash; you
              won&apos;t be able to see it again
            </li>
          </ol>
          <Divider style={{ margin: "12px 0" }} />
          <Text type="secondary">
            For more details, see the{" "}
            <Link href={GITHUB_DOCS_URL} target="_blank" rel="noopener noreferrer">
              GitHub documentation on managing personal access tokens
            </Link>
            .
          </Text>
        </Card>

        <div>
          <Text strong style={{ display: "block", marginBottom: 8 }}>
            Paste your token below:
          </Text>
          <Space.Compact style={{ width: "100%" }}>
            <Input.Password
              prefix={<KeyOutlined />}
              placeholder="ghp_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
              value={patInput}
              onChange={(e) => setPatInput(e.target.value)}
              onPressEnter={handleSave}
              size="large"
              style={{ fontFamily: "monospace" }}
            />
            <Button
              type="primary"
              size="large"
              loading={saving}
              onClick={handleSave}
              disabled={!patInput.trim()}
            >
              Save Token
            </Button>
          </Space.Compact>
          <Text type="secondary" style={{ display: "block", marginTop: 8 }}>
            Your token is stored securely on the server and never exposed to the browser after
            saving.
          </Text>
        </div>
      </Space>
    </Card>
  );
}

export default GitHubPatSetup;
